import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TrainingCertVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState('');
  const [valid, setValid] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    verify();
  }, [token]);

  const verify = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/training/verify/${token}`);
      if (response.data.success && response.data.valid) {
        setCertificate(response.data.certificate);
        setValid(true);
        setCanDownload(response.data.can_download);
        setDownloadMessage(response.data.download_message);
      } else {
        setError(response.data.message || 'Verification failed');
        setValid(false);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed. Certificate not found.';
      setError(msg);
      setValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!canDownload) {
      alert(downloadMessage);
      return;
    }
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/training/verify/${token}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TrainingCertificate_${certificate.certNo || token}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Refresh to update download count
      await verify();
      alert('Certificate downloaded successfully!');
    } catch (err) {
      if (err.response && err.response.status === 429) {
        alert(err.response.data?.message || 'Daily download limit reached. Please try again tomorrow.');
      } else {
        alert('Error downloading certificate. Please try again.');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#1a472a', color: '#fff', padding: '20px', textAlign: 'center' }}>
        <h2 style={{ margin: 0 }}>Training Certificate Verification</h2>
        <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: '0.9rem' }}>
          Dynavative Indo Veritas Safety Services
        </p>
      </div>

      <div style={{ maxWidth: '650px', margin: '40px auto', padding: '0 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{
              width: '48px', height: '48px', border: '5px solid #e9ecef',
              borderTop: '5px solid #1a472a', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 16px'
            }} />
            <p style={{ color: '#6c757d' }}>Verifying certificate…</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : valid && certificate ? (
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px',
            border: '2px solid #198754', overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
          }}>
            {/* Valid banner */}
            <div style={{ backgroundColor: '#198754', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '1.8rem' }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Certificate Verified</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>This training certificate is authentic</div>
              </div>
            </div>

            {/* Details */}
            <div style={{ padding: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Course', certificate.courseName],
                    ['Certificate Type', certificate.certType],
                    ['Trainee Name', certificate.traineeName],
                    ['Certificate No.', certificate.certNo],
                    ['Issue Date', certificate.issueDate ? formatDate(certificate.issueDate) : '—'],
                    ['Valid Until', certificate.validUntil ? formatDate(certificate.validUntil) : '—'],
                    ['Total Verifications', certificate.totalVerifications],
                  ].map(([label, value]) => (
                    <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 8px', color: '#6c757d', fontWeight: 600, width: '42%', fontSize: '0.9rem' }}>
                        {label}
                      </td>
                      <td style={{ padding: '10px 8px', fontWeight: 500, fontSize: '0.9rem' }}>
                        {value !== null && value !== undefined && value !== '' ? value : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Download Section */}
            <div style={{ padding: '16px 24px', textAlign: 'center', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={handleDownload}
                disabled={!canDownload || downloading}
                style={{
                  backgroundColor: canDownload ? '#0d6efd' : '#6c757d',
                  color: '#fff', border: 'none', borderRadius: '6px',
                  padding: '10px 28px', fontSize: '1rem', cursor: canDownload ? 'pointer' : 'not-allowed',
                  opacity: downloading ? 0.75 : 1
                }}
              >
                {downloading ? '⏳ Downloading…' : '⬇️ Download Certificate'}
              </button>
              {!canDownload && downloadMessage && (
                <p style={{ color: '#e67e22', fontSize: '0.82rem', marginTop: '8px', marginBottom: 0 }}>
                  ⚠️ {downloadMessage}
                </p>
              )}
              <p style={{ color: '#6c757d', fontSize: '0.78rem', marginTop: '8px', marginBottom: 0 }}>
                ℹ️ Downloads are limited to 3 per day for security purposes
              </p>
            </div>

            <div style={{ backgroundColor: '#f8f9fa', padding: '12px 24px', fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
              🔒 This is a secure verification page. All verifications are logged.
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px',
            border: '2px solid #dc3545', overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
          }}>
            <div style={{ backgroundColor: '#dc3545', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '1.8rem' }}>❌</span>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Verification Failed</div>
            </div>
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>⚠️</div>
              <p style={{ fontWeight: 600, marginBottom: '6px' }}>Invalid verification code. Certificate not found.</p>
              <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>{error}</p>
            </div>
            <div style={{ backgroundColor: '#f8f9fa', padding: '12px 24px', fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
              🔒 This is a secure verification page. All certificate verifications are logged.
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              backgroundColor: '#1a472a', color: '#fff', border: 'none',
              borderRadius: '6px', padding: '10px 24px', cursor: 'pointer', fontSize: '0.9rem'
            }}
          >
            🏠 Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB');
};

export default TrainingCertVerification;
